// Bibliothèque de documents : recherche, classement, versions, prévisualisation PDF et images,
// téléchargement par URL signée, archivage (L0-C).
import type { DocumentResume } from '@cc/contrats/ui-metier';
import { Bandeau, Bouton, Champ, ConfirmationIntegree, EtatVide, Panneau, Selection, Tableau, tailleLisible, type ColonneTableau } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { urlContenu, useListePaginee } from './listes';
import commun from '../../commun/commun.module.css';
import styles from './metier.module.css';

function Apercu({ document: d }: { document: DocumentResume }) {
  const { contexte } = useSession();
  const [url, setUrl] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => {
    let actif = true;
    setUrl(null);
    setErreur(null);
    urlContenu(d.id).then((u) => actif && setUrl(u)).catch((e: unknown) => actif && setErreur(messageErreur(e)));
    return () => {
      actif = false;
    };
  }, [d.id]);
  const archivage = useAction({
    executer: () => requete(`/documents/${d.id}/archiver`, { methode: 'POST' }),
    succes: 'Document archivé.',
    invalider: [['documents']],
  });
  const image = d.type_mime?.startsWith('image/');
  const pdf = d.type_mime === 'application/pdf';
  return (
    <Panneau titre={d.titre} actions={url ? <a className={commun.lienSimple} href={url} download={d.titre}><Download aria-hidden width={16} height={16} /> Télécharger</a> : null}>
      <dl className={commun.definitions}>
        <dt>Version</dt>
        <dd>{d.version_courante}</dd>
        <dt>Type</dt>
        <dd>{d.type_mime}</dd>
        <dt>Taille</dt>
        <dd>{d.taille ? tailleLisible(d.taille) : 'non renseigné'}</dd>
        <dt>Empreinte SHA-256</dt>
        <dd className={commun.na}>{d.empreinte_sha256}</dd>
        <dt>Classement</dt>
        <dd>{d.classement_id ?? 'non classé'}</dd>
      </dl>
      {erreur ? <Bandeau type="erreur">Contenu inaccessible : {erreur}</Bandeau> : null}
      {url && pdf ? <iframe className={styles.apercu} src={url} title={`Aperçu de ${d.titre}`} /> : null}
      {url && image ? <img className={styles.apercuImage} src={url} alt={`Aperçu de ${d.titre}`} /> : null}
      {url && !pdf && !image ? <p>Aperçu non disponible pour ce type de fichier : utilisez le téléchargement.</p> : null}
      {!d.archive_le && peut(contexte, 'document', 'archiver') ? (
        <ConfirmationIntegree libelle="Archiver" question="Archiver ce document ? Il reste consultable mais ne peut plus être modifié." libelleConfirmer="Confirmer l'archivage" enCours={archivage.enCours} onConfirmer={() => archivage.lancer(undefined)} />
      ) : null}
      {archivage.messageErreur ? <Bandeau type="erreur">{archivage.messageErreur}</Bandeau> : null}
    </Panneau>
  );
}

export function PageDocuments() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [recherche, setRecherche] = useState('');
  const [classement, setClassement] = useState('');
  const classements = useQuery({
    queryKey: ['classements'],
    queryFn: ({ signal }) => requete<{ elements: { id: string; libelle: string }[] }>('/classements', { signal }).then((r) => r.elements),
    retry: false,
  });
  const liste = useListePaginee<DocumentResume>('documents', '/documents', { q: recherche, classement_id: classement });
  const choisi = params.get('document');
  const selection = liste.elements.find((d) => d.id === choisi) ?? null;

  const colonnes: ColonneTableau<DocumentResume>[] = [
    {
      id: 'titre',
      entete: 'Titre',
      enTeteLigne: true,
      masquable: false,
      valeurTri: (d) => d.titre,
      cellule: (d) => (
        <Bouton petit variante="discret" icone={FileText} onClick={() => setParams({ document: d.id })} aria-label={`Ouvrir ${d.titre}`}>
          {d.titre}
        </Bouton>
      ),
    },
    { id: 'type', entete: 'Type', cellule: (d) => d.type_document },
    { id: 'version', entete: 'Version', numerique: true, cellule: (d) => d.version_courante, valeurTri: (d) => d.version_courante },
    { id: 'taille', entete: 'Taille', numerique: true, cellule: (d) => tailleLisible(d.taille), valeurTri: (d) => d.taille },
    { id: 'date', entete: 'Déposé le', cellule: (d) => dateHeure(d.cree_le), valeurTri: (d) => d.cree_le },
    { id: 'archive', entete: 'Archivé', cellule: (d) => (d.archive_le ? 'Oui' : 'Non') },
  ];

  return (
    <>
      <EnTetePage etiquette="Gestion électronique des documents" titre="Documents" description="Pièces, actes et documents produits. Chaque fichier est identifié par son empreinte et ses versions sont conservées." />
      <form className={commun.ligne} style={{ alignItems: 'flex-end' }} role="search" onSubmit={(e) => { e.preventDefault(); setRecherche(q.trim()); }}>
        <Champ libelle="Rechercher (titre, texte reconnu)" type="search" value={q} onChange={(e) => setQ(e.target.value)} />
        {classements.data ? (
          <Selection libelle="Classement" value={classement} onChange={(e) => setClassement(e.target.value)} optionVide="Tous les classements" options={classements.data.map((c) => ({ valeur: c.id, libelle: c.libelle }))} />
        ) : null}
        <Bouton type="submit" variante="secondaire">Rechercher</Bouton>
      </form>
      <EtatRequete chargement={liste.requete.isPending} erreur={liste.requete.error} onReessayer={() => void liste.requete.refetch()} lot="L0-C" fonction="La bibliothèque de documents">
        {liste.elements.length === 0 ? (
          <EtatVide titre="Aucun document" icone={FileText}>
            <p>Aucun document ne correspond à cette recherche.</p>
          </EtatVide>
        ) : (
          <div className={commun.deuxColonnes}>
            <Tableau legende="Documents" colonnes={colonnes} lignes={liste.elements} cleLigne={(d) => d.id} cleColonnes="documents" pagination={liste.pagination} />
            {selection ? <Apercu document={selection} /> : <EtatVide titre="Aucun document ouvert"><p>Choisissez un document pour l&apos;afficher.</p></EtatVide>}
          </div>
        )}
      </EtatRequete>
    </>
  );
}
